/**
 * OptimierungSuggestionCard — Kern-Einheit von Zustand 3 (Claude-Design drawer-v2).
 * Diff (ALT↔NEU) + „Warum"-Zeile + optionale Amber-Warnung + Aktionen.
 *
 * Additive Erweiterung (E4/T7, Bulk-Text-Review): `reasoning`, `pushStatus`,
 * `onEdit`, `editSlot` sind optional — ohne sie verhält sich die Karte exakt
 * wie im Optimierungen-Drawer. `cost`/`onRegenerate` sind ebenfalls optional
 * (die Review-Queue kennt keine Pro-Feld-Kosten und kein Regenerieren).
 */
import type { ReactNode } from 'react'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ContentFieldSuggestion } from '@/lib/api/channel-content'

import { DV2 } from './tokens'
import { attrLabel } from './labels'
import { DiffBlock, WarumLine } from './suggestionParts'
import { PushStatusPill, ReasoningLine, RuleComplianceWarning } from './SuggestionCardExtras'
import { SuggestionCardActions } from './SuggestionCardActions'

export interface OptimierungSuggestionCardProps {
  field: ContentFieldSuggestion
  cost?: number
  channelName: string
  busy?: boolean
  /** Eindeutiger Suffix für die EID-Registry-IDs (Default: attribute_code — reicht im Drawer). */
  idSuffix?: string
  /** Freitext der KI, warum der Vorschlag so aussieht (Bulk-Text-Review, Q3). */
  reasoning?: string | null
  /** Push-Ergebnis nach Übernehmen (Bulk-Text-Review). */
  pushStatus?: 'ok' | 'failed' | null
  onApply: () => void
  onRegenerate?: () => void
  onDiscard: () => void
  /** Öffnet den Inline-Editor (Bulk-Text-Review). Ohne Handler kein Bearbeiten-Button. */
  onEdit?: () => void
  /** Wenn gesetzt, ersetzt dieser Slot den NEU-Diff-Block (Inline-Edit-Textarea). */
  editSlot?: ReactNode
}

export function OptimierungSuggestionCard({
  field,
  cost,
  channelName,
  busy = false,
  idSuffix = field.attribute_code,
  reasoning,
  pushStatus,
  onApply,
  onRegenerate,
  onDiscard,
  onEdit,
  editSlot,
}: OptimierungSuggestionCardProps) {
  const { t } = useTranslation()
  return (
    <div className={`rounded-xl border ${DV2.line} ${DV2.card} overflow-hidden`}>
      <div className={`flex items-center gap-2 border-b ${DV2.line} px-3.5 py-2.5`}>
        <Sparkles size={12} className="text-primary-500" />
        <span className={`text-sm-minus font-semibold ${DV2.fg1}`}>{attrLabel(field.attribute_code)}</span>
        <span className={`font-mono text-2xs ${DV2.fg4}`}>{field.attribute_code}</span>
        <div className="ml-auto flex items-center gap-2">
          {pushStatus && <PushStatusPill status={pushStatus} />}
          {cost !== undefined && <span className={`font-mono text-2xs ${DV2.fg3}`}>{cost} Cr</span>}
        </div>
      </div>

      <div className="space-y-3 px-3.5 py-3">
        <WarumLine rules={field.triggered_rules} channelName={channelName} />
        <DiffBlock variant="alt" label={t('channelContent.card.old')} value={field.previous} />
        {editSlot ?? <DiffBlock variant="neu" label={t('channelContent.card.new')} value={field.suggestion} />}

        {reasoning && <ReasoningLine text={reasoning} />}
        {!field.rule_compliant && <RuleComplianceWarning />}

        <SuggestionCardActions
          idSuffix={idSuffix}
          busy={busy}
          onApply={onApply}
          onDiscard={onDiscard}
          onEdit={onEdit}
          onRegenerate={onRegenerate}
        />
      </div>
    </div>
  )
}
